import { useCallback } from "react"
import { useMutation } from "urql"

import type { ProductTestOverrideResultsPayload } from "../types"

import { useUpdateProductTestDocuments } from "./useUpdateProductTestDocuments"

import type {
  ApproveQualityApprovalMutation,
  QualityApprovalApproveFailure,
  QualityApprovalApproveInput,
  QualityApprovalApproveSuccess,
} from "@/graphql/codegen/graphql"
import { ApproveQualityApprovalDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseApproveQualityApprovalReturn {
  approveQualityApproval: (
    input: QualityApprovalApproveInput,
    attachments?: Pick<ProductTestOverrideResultsPayload, "documentsToUpload" | "documentsToCopy">
  ) => Promise<boolean>
  isApproving: boolean
}

export const useApproveQualityApproval = (): UseApproveQualityApprovalReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { updateDocuments, isUpdatingDocuments } = useUpdateProductTestDocuments()
  const [{ fetching }, executeMutation] = useMutation<
    ApproveQualityApprovalMutation,
    { input: QualityApprovalApproveInput }
  >(ApproveQualityApprovalDocument)

  const approveQualityApproval = useCallback(
    async (
      input: QualityApprovalApproveInput,
      attachments?: Pick<ProductTestOverrideResultsPayload, "documentsToUpload" | "documentsToCopy">
    ): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.qualityApproval.approve

      if (error || output?.__typename === "QualityApprovalApproveFailure") {
        const message = error?.message ?? (output as QualityApprovalApproveFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      const { productTestId } = (output as QualityApprovalApproveSuccess).qualityApproval.productTestResult!

      if (attachments) {
        await updateDocuments({
          productTestId,
          ...attachments,
          isLotUpdated: input.isLotUpdated,
        })
      }

      notify({ type: NotificationType.Success, message: "Out of spec approval request approved" })

      return true
    },
    [executeMutation, updateDocuments, notify]
  )

  return { approveQualityApproval, isApproving: fetching || isUpdatingDocuments }
}
